import React, { useState } from 'react';
import styled from 'styled-components';
import Header from './components/Header';
import Gallery from './components/Gallery';
import About from './components/About';
import Process from './components/Process';
import Subscribe from './components/Subscribe';
import Footer from './components/Footer';
import Upcoming from './components/Upcoming';
import GlobalStyles from './styles/GlobalStyles';
import './styles/ResponsiveEnhancements.css';

const AppContainer = styled.div`
  min-height: 100vh;
  background: 
    radial-gradient(circle at 15% 25%, rgba(212, 175, 55, 0.008) 0%, transparent 50%),
    radial-gradient(circle at 85% 75%, rgba(212, 175, 55, 0.006) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.004) 0%, transparent 70%),
    linear-gradient(135deg, var(--color-obsidian) 0%, var(--color-charcoal) 25%, var(--color-obsidian) 50%, var(--color-charcoal) 75%, var(--color-obsidian) 100%);
  color: #ffffff;
  font-family: 'Inter', 'Arial', sans-serif;
  position: relative;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.003) 0%, transparent 60%),
      radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.002) 0%, transparent 60%);
    pointer-events: none;
    z-index: 0;
    animation: luxury-ambient-glow 20s ease-in-out infinite;
  }
`;

const FloatingOrb = styled.div`
  position: fixed;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(212, 175, 55, 0.06), transparent 70%);
  backdrop-filter: blur(20px);
  animation: luxury-orb-float 15s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
  
  &:nth-child(1) {
    width: 120px;
    height: 120px;
    top: 15%;
    left: 5%;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    width: 80px;
    height: 80px;
    top: 60%;
    right: 8%;
    animation-delay: -5s;
  }
  
  &:nth-child(3) {
    width: 60px;
    height: 60px;
    top: 30%;
    left: 80%;
    animation-delay: -10s;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

export interface CartItem {
  id: number;
  name: string;
  price: number;
  scale: string;
  image: string;
  quantity: number;
}

function App() {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState<'home' | 'shop'>('home');

  const handleAddToCart = (product: { id: number; name: string; price: number; scale: string; image: string }) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prevItems, { ...product, quantity: 1 }];
    });
    setIsCartOpen(true); // Open cart immediately when item is added
  };

  const handleUpdateCartQuantity = (id: number, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveFromCart(id);
      return;
    }
    setCartItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const handleRemoveFromCart = (id: number) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const totalCartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <AppContainer>
      <GlobalStyles />
      <FloatingOrb />
      <FloatingOrb />
      <FloatingOrb />
      <ContentWrapper>
        <Header 
          cartCount={totalCartCount} 
          onCartClick={() => setIsCartOpen(true)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
        {currentPage === 'home' ? (
          <>
            <Gallery 
              isFeaturedOnly={true}
              searchTerm=""
              cartItems={cartItems}
              isCartOpen={isCartOpen}
              onCloseCart={() => setIsCartOpen(false)}
              onAddToCart={handleAddToCart}
              onUpdateCartQuantity={handleUpdateCartQuantity}
              onRemoveFromCart={handleRemoveFromCart}
              onOpenCart={() => setIsCartOpen(true)}
              setViewShopPage={() => setCurrentPage('shop')}
            />
            <Upcoming />
            <About />
            <Process />
            <Subscribe />
          </>
        ) : (
          <Gallery 
            isFeaturedOnly={false}
            searchTerm={searchTerm}
            cartItems={cartItems}
            isCartOpen={isCartOpen}
            onCloseCart={() => setIsCartOpen(false)}
            onAddToCart={handleAddToCart}
            onUpdateCartQuantity={handleUpdateCartQuantity}
            onRemoveFromCart={handleRemoveFromCart}
            onOpenCart={() => setIsCartOpen(true)}
          />
        )}
        <Footer />
      </ContentWrapper>
    </AppContainer>
  );
}

export default App;

